import { useMutation, useReactiveVar } from '@apollo/client';
import { Typography } from '@mui/material';
import type React from 'react';
import deleteProfile from 'src/api/profile/deleteProfile';
import usersProfiles from 'src/api/profile/usersProfiles';
import AkomiDialog from 'src/components/dialogs/AkomiDialog';
import AkomiDialogActions from 'src/components/dialogs/AkomiDialogActions';
import { useCustomToast } from 'src/hooks/menu';
import viewStateVar from 'src/store/viewState';

interface DeleteProfileVariables {
  profileId: string;
}

export default function UsersDeleteModal(): React.ReactElement {
  const viewState = useReactiveVar(viewStateVar);
  const { toastr } = useCustomToast();

  const [deleteProfileReq] = useMutation<unknown, DeleteProfileVariables>(deleteProfile);

  const cancelDeleteHandler = (): void => {
    viewStateVar({
      ...viewStateVar(),
      usersDeleteDialog: null,
    });
  };

  const deleteUsersHandler = (): void => {
    if (viewState.selectedRowsUserIds.length === 0) {
      deleteProfileReq({
        variables: {
          profileId: viewState.usersDeleteDialog as string,
        },
        refetchQueries: [usersProfiles],
      }).then(() => {
        toastr.success(`User ${viewState.selectedDeleteUsername} deleted successfully`);
        viewStateVar({
          ...viewStateVar(),
          usersDeleteDialog: null,
        });
      });
    } else {
      for (let i = 0; i < viewState.selectedRowsUserIds.length; i++) {
        deleteProfileReq({
          variables: {
            profileId: viewState.selectedRowsUserIds[i],
          },
          refetchQueries: [usersProfiles],
        }).then(() => {
          if (i !== viewState.selectedRowsUserIds.length) {
            toastr.success(`${viewState.selectedRowsUsernames[i]} was deleted successfully`);
            viewStateVar({
              ...viewStateVar(),
              usersDeleteDialog: null,
              selectedRowsUserIds: [],
            });
          }
        });
      }
    }
  };

  return (
    <AkomiDialog
      title="Delete"
      maxWidth="xs"
      open={!!viewState.usersDeleteDialog}
      onClose={cancelDeleteHandler}
      actions={
        <AkomiDialogActions
          variant="delete-cancel"
          primaryLabel="Delete"
          onPrimary={deleteUsersHandler}
          onSecondary={cancelDeleteHandler}
        />
      }
    >
      <Typography>
        {viewState.selectedRowsUserIds.length === 0
          ? `Are you sure you want to delete the user ${viewState.selectedDeleteUsername}?`
          : `Are you sure you want to delete ${viewState.selectedRowsUserIds.length} users?`}
      </Typography>
    </AkomiDialog>
  );
}
